import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { is3pProductKey, ProductKeys } from '../../constants/product-keys';
import {
	AUTH_RESOLUTION_MODE_ADMIN_IMPORTED,
	FEDERATED_CONNECTOR_SOURCE,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	SMARTLINKS_CONNECTOR_SOURCE,
	SMARTLINKS_RESOURCE_TYPE_PREFIX,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../constants/schemas/3p-config';
import {
	serveSharepointImpersonatedSearchExpWithoutExposure,
	serveTeamsImpersonatedSearchExpWithoutExposure,
} from '../experiments';
import { hasImpersonatedFullConnector } from '../impersonation';

/**
 * Products for which 3LO (user OAuth) has been removed.
 * The Connect button will be hidden for these products when the admin has
 * configured a full connector, since the user no longer needs to authenticate.
 * Add more product keys here as 3LO removal is completed for each product.
 */
const PRODUCTS_WITH_3LO_REMOVAL: ReadonlySet<ProductKeys3P> = new Set([
	ProductKeys3P.GoogleDrive,
	ProductKeys3P.Github,
]);

const shouldTreatImpersonatedFullConnectorAsConnected = (productKey: string): boolean => {
	if (productKey === ProductKeys.Teams) {
		return serveTeamsImpersonatedSearchExpWithoutExposure();
	}
	if (productKey === ProductKeys.Sharepoint) {
		return serveSharepointImpersonatedSearchExpWithoutExposure();
	}

	return (
		PRODUCTS_WITH_3LO_REMOVAL.has(productKey as ProductKeys3P) &&
		fg('ai_3p_remove_connect_button_for_allowlist_product')
	);
};

interface isProductSmartlinkProps {
	productKey?: string; // loosely typed as string to accommodate for few cases which have this value as a string
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}

// `connectorSyncType` and `type` are spread onto each datasource from the raw API resource but are
// absent from the typed shape, hence the casts. Same precedent as `getDatasourceConnectorSource`.
const isFederatedDatasource = (datasource: ThirdPartyConfigsWithAPIResponse): boolean =>
	(datasource as { connectorSyncType?: string | null }).connectorSyncType ===
	FEDERATED_CONNECTOR_SOURCE;

// Must read `type`, not `resourceType`: the latter is inherited from the static Slack config and
// reads `slack-connector` for both variants.
const isSmartlinksDatasource = (datasource: ThirdPartyConfigsWithAPIResponse): boolean =>
	(datasource as { type?: string }).type?.startsWith(SMARTLINKS_RESOURCE_TYPE_PREFIX) ?? false;

/**
 * The datasource whose OAuth state should drive a product's connect affordance, when a
 * lower-priority connector is masking a higher-priority one. `undefined` means "no override" — the
 * caller's usual product-level resolution applies.
 *
 * A product can expose several connectors at once and they all merge onto one product key, so the
 * product-level OAuth fields reflect whichever resource was processed last while only `datasources`
 * keeps the per-connector truth. The higher-priority connector should decide, because it is the one
 * that yields results.
 *
 * Today this resolves Slack only — federated over SmartLinks, see HOT-305437. The intended
 * generalisation is `full > federated > smartlinks` across products; widening it means changing the
 * policy here, not any caller.
 */
export const getPreferredConnectorDatasource = (
	productKey: string | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): ThirdPartyConfigsWithAPIResponse | undefined => {
	if (productKey !== ProductKeys3P.Slack) {
		return undefined;
	}

	const datasources = thirdPartyConfigs[ProductKeys3P.Slack]?.datasources;
	const federatedDatasource = datasources?.find(isFederatedDatasource);
	if (!federatedDatasource || !datasources?.some(isSmartlinksDatasource)) {
		return undefined;
	}

	// Gate last so the exposure is scoped to the affected population.
	return fg('ai_3p_slack_federated_connect_cta_enabled') ? federatedDatasource : undefined;
};

/**
 * Returns whether an admin has imported user links for a full connector.
 */
export const hasAdminImportedUserLink = (
	productKey: string | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	if (!productKey) {
		return false;
	}
	const productConfig = thirdPartyConfigs[productKey as ProductKeys3P];
	return Boolean(
		productConfig?.connectorSources?.includes(FULL_CONNECTOR_SOURCE) &&
		productConfig.datasources?.some(
			(datasource) => datasource.authResolutionMode === AUTH_RESOLUTION_MODE_ADMIN_IMPORTED,
		) &&
		// Gate last so the exposure is scoped to the affected population.
		fg('ai_3p_cds_auth_resolution_mode_flock'),
	);
};

/**
 * Reported when a connector's auth resolution mode cannot be determined, either because the CDS
 * gate is off upstream (the API omits the field) or because the result's datasource is absent from
 * the config.
 *
 * Emitted rather than omitted so the two cases stay distinguishable downstream: an absent attribute
 * means this instrumentation is not deployed, `UNKNOWN` means it is live but the mode is unavailable.
 */
export const AUTH_RESOLUTION_MODE_UNKNOWN = 'UNKNOWN' as const;

/**
 * The `authResolutionMode` to report for a single result.
 *
 * Datasource-level values win because every connector a product exposes merges onto one product
 * key, leaving the product-level field reflecting whichever resource was processed last while
 * `datasources` keeps the per-connector truth (same reasoning as `getPreferredConnectorDatasource`).
 * Falls back to the product level when the datasource is unknown or carries no mode, then to
 * `AUTH_RESOLUTION_MODE_UNKNOWN`.
 *
 * The matched value is passed through without validating it against the known modes: a mode added
 * upstream should surface as itself rather than being flattened into `UNKNOWN`, which would be
 * indistinguishable from the mode being unavailable.
 */
export const getAuthResolutionMode = ({
	datasourceId,
	productKey,
	thirdPartyConfigs,
}: {
	datasourceId?: string;
	productKey?: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): string => {
	const productConfig = productKey ? thirdPartyConfigs[productKey as ProductKeys3P] : undefined;

	if (datasourceId) {
		// Scoped to the product when known so a datasource id cannot resolve against an unrelated
		// product; without a product key the whole config is the only place left to look.
		const datasources = productKey
			? productConfig?.datasources
			: Object.values(thirdPartyConfigs).flatMap((config) => config?.datasources ?? []);
		const matchedDatasource = datasources?.find(
			(datasource) => datasource?.datasourceId === datasourceId,
		);
		if (matchedDatasource?.authResolutionMode) {
			return matchedDatasource.authResolutionMode;
		}
	}

	return productConfig?.authResolutionMode ?? AUTH_RESOLUTION_MODE_UNKNOWN;
};

// TODO: Consolidate the Forge OAuth-state logic duplicated between `isProductConnected` and
// `isUserOAuthed` in `src/controllers/oauth/index.tsx`.
/**
 * Determines whether a product is "connected" (i.e. the user has completed OAuth where required).
 * First-party products (not in thirdPartyConfigs) are always considered connected.
 *
 * For third-party products, userNeedsOAuth indicates whether user needs to authenticate:
 * - userNeedsOAuth: true → NOT connected (needs OAuth)
 * - userNeedsOAuth: false → IS connected (OAuth completed)
 * Forge connectors carry this state at product level because they have no datasources.
 */
export const isProductConnected = (
	productKey: string | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	if (!productKey) {
		return true;
	}
	const productConfig = thirdPartyConfigs[productKey as ProductKeys3P];
	if (!productConfig) {
		// For allowlisted products with the gate ON, treat missing config as not connected
		// (the API returned no resource for this product, so no full connector is configured)
		if (
			PRODUCTS_WITH_3LO_REMOVAL.has(productKey as ProductKeys3P) &&
			fg('ai_3p_remove_connect_button_for_allowlist_product') // must be last for exposure tracking
		) {
			return false;
		}
		return true;
	}
	// Forge connector configs carry OAuth state at the product level and have no
	// `datasources`, so the datasource-based derivation below would always report
	// them as not connected.
	if (isForgeConnectorProductKey(productKey)) {
		return productConfig.userNeedsOAuth === false;
	}
	// Full connector admin-configured AND impersonation enabled → no user OAuth needed for products with 3LO removed
	// Check datasources directly rather than product config root, since impersonationEnabled at the
	// product config level is overwritten by the last resource processed and may be stale.
	// SmartLinks/lite-connector resources never have impersonationEnabled: true, so checking any
	// datasource is safe and effectively scopes to full-connector resources only.
	if (
		hasImpersonatedFullConnector(productKey, thirdPartyConfigs) &&
		shouldTreatImpersonatedFullConnectorAsConnected(productKey)
	) {
		return true;
	}
	if (hasAdminImportedUserLink(productKey, thirdPartyConfigs)) {
		return true;
	}

	const preferredDatasource = getPreferredConnectorDatasource(productKey, thirdPartyConfigs);
	if (preferredDatasource) {
		return !preferredDatasource.userNeedsOAuth;
	}
	return productConfig.datasources?.some((ds) => !ds?.userNeedsOAuth) ?? false;
};

export const isProductSmartlink = ({
	productKey,
	thirdPartyConfigs,
}: isProductSmartlinkProps): boolean => {
	const product3PConfig =
		productKey && is3pProductKey(productKey) ? thirdPartyConfigs[productKey] : null;
	return (
		product3PConfig?.connectorSources?.length === 1 &&
		product3PConfig.connectorSources[0] === SMARTLINKS_CONNECTOR_SOURCE
	);
};
